import { useEffect, useMemo, useRef, useState } from 'react';

import { useDayJs } from '@common/hooks';

interface DayLeftTimerResult {
  formatted_time: string;
  is_ended: boolean;
  total_seconds: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface UseDayLeftTimerProps {
  end_timestamp?: number | null;
  timer_threshold_days?: number;
  enable_smooth_transition?: boolean;
}

export const useDayLeftTimer = ({
  end_timestamp,
  timer_threshold_days = 1,
  enable_smooth_transition = true,
}: UseDayLeftTimerProps): DayLeftTimerResult => {
  const dayjs = useDayJs();
  const [current_time, setCurrentTime] = useState<number | null>(null);
  const interval_ref = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!current_time) {
      setCurrentTime(dayjs().valueOf());
    }
  }, [dayjs]);

  const time_left = useMemo(() => {
    if (!end_timestamp || !current_time) {
      return {
        total_seconds: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        is_ended: true,
        is_loading: true,
      };
    }

    const end_time = dayjs(end_timestamp);
    const now = dayjs(current_time);
    const diff_ms = end_time.diff(now);

    if (diff_ms <= 0) {
      return {
        total_seconds: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        is_ended: true,
      };
    }

    const total_seconds = Math.floor(diff_ms / 1000);
    const days = Math.floor(total_seconds / (24 * 60 * 60));
    const hours = Math.floor((total_seconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((total_seconds % (60 * 60)) / 60);
    const seconds = total_seconds % 60;

    return {
      total_seconds,
      days,
      hours,
      minutes,
      seconds,
      is_ended: false,
    };
  }, [dayjs, end_timestamp, current_time]);

  const formatted_time = useMemo(() => {
    if (time_left.is_loading) {
      return '';
    }

    if (time_left.is_ended) {
      return '종료';
    }

    // 1. threshold_days 이상 남은 경우: {n}일만 표시
    if (time_left.days >= timer_threshold_days) {
      return `${time_left.days}일 남음`;
    }

    // 2. threshold_days 미만 & 1일 이상 남은 경우: {n일} 23:59:59 (잔여일 + 타이머)
    if (time_left.days >= 1) {
      const hours_string = time_left.hours.toString().padStart(2, '0');
      const minutes_string = time_left.minutes.toString().padStart(2, '0');
      const seconds_string = time_left.seconds.toString().padStart(2, '0');
      return `${time_left.days}일 ${hours_string}:${minutes_string}:${seconds_string} 남음`;
    }

    // 3. 1일 미만 남은 경우 (24시간 미만): 23:59:59 (타이머만 노출)
    const hours_string = time_left.hours.toString().padStart(2, '0');
    const minutes_string = time_left.minutes.toString().padStart(2, '0');
    const seconds_string = time_left.seconds.toString().padStart(2, '0');
    return `${hours_string}:${minutes_string}:${seconds_string} 남음`;
  }, [time_left, timer_threshold_days]);

  // 최적화된 interval 값 계산
  const optimal_interval = useMemo(() => {
    if (time_left.is_ended) {
      return null;
    }

    // threshold 이상 남은 경우
    if (time_left.days >= timer_threshold_days) {
      // smooth transition이 활성화된 경우에만 경계선 최적화 적용
      if (enable_smooth_transition) {
        // threshold 경계 매우 근처 (threshold일 + 1분 이내)에서는 1초마다 체크
        if (time_left.days === timer_threshold_days && time_left.hours === 0 && time_left.minutes === 0) {
          return 1000; // 1초
        }
        // threshold 경계 근처 (threshold일 + 1시간 이내)에서는 1분마다 체크
        if (time_left.days === timer_threshold_days && time_left.hours === 0) {
          return 60 * 1000; // 1분
        }
      }
      // 그 외에는 1시간마다 체크 (타이머 미노출)
      return 60 * 60 * 1000; // 1시간
    }

    // threshold 미만 남은 경우: 1초마다 업데이트 (타이머 노출)
    return 1000;
  }, [
    time_left.is_ended,
    time_left.days,
    time_left.hours,
    time_left.minutes,
    timer_threshold_days,
    enable_smooth_transition,
  ]);

  useEffect(() => {
    if (interval_ref.current) {
      clearInterval(interval_ref.current);
      interval_ref.current = null;
    }

    if (optimal_interval === null) {
      return;
    }

    interval_ref.current = setInterval(() => {
      setCurrentTime(dayjs().valueOf());
    }, optimal_interval);

    return () => {
      if (interval_ref.current) {
        clearInterval(interval_ref.current);
        interval_ref.current = null;
      }
    };
  }, [dayjs, optimal_interval]);

  return {
    formatted_time,
    is_ended: time_left.is_ended,
    total_seconds: time_left.total_seconds,
    days: time_left.days,
    hours: time_left.hours,
    minutes: time_left.minutes,
    seconds: time_left.seconds,
  };
};
